import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";

import WorkflowRunsPage, {
  type WorkflowRunsColumn,
} from "src/views/products/workflow/pages/WorkflowRunsPage";
import { downloadReportArtifact } from "src/views/assistant/records/downloadReportArtifact";
import { useResumeRecordWithAgent } from "src/views/assistant/records/useResumeRecordWithAgent";
import WorkflowRunsEmptyState from "src/views/products/workflow/components/WorkflowRunsEmptyState";
import { FIFTY_FIFTY_NINE_PRODUCT } from "./fiftyFiftyNineProduct";
import FfnRecordDetailDialog, {
  VERIFICATION_TIER_COLOR,
} from "./components/FfnRecordDetailDialog";
import {
  useFfnPlants,
  useFfnRecords,
  verifyFfnRecordReport,
  type FfnRecordRow,
} from "./services/FfnService";

const STATUS_OPTIONS = [
  "draft",
  "screening",
  "evaluation",
  "complete",
  "screened_out",
  "not_applicable",
  "cancelled",
] as const;

const STATUS_CHIP_COLOR: Record<
  string,
  "default" | "primary" | "success" | "warning"
> = {
  complete: "success",
  screened_out: "success",
  not_applicable: "default",
  cancelled: "default",
  draft: "default",
  screening: "primary",
  evaluation: "primary",
};

/** The 50.59 records dashboard on the shared workflow runs frame. */
const FfnRecordsDashboardPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [plantFilter, setPlantFilter] = useState<number | "">("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selected, setSelected] = useState<FfnRecordRow | null>(null);
  const [verifyingRecordId, setVerifyingRecordId] = useState<number | null>(
    null,
  );

  const { data: plants = [] } = useFfnPlants();
  const {
    data: records = [],
    isFetching,
    isError,
    refetch,
  } = useFfnRecords({
    plant_id: plantFilter === "" ? undefined : plantFilter,
    status: statusFilter || undefined,
  });
  const { resumeRecord, resumingRecordId } = useResumeRecordWithAgent();

  const handleVerify = async (recordId: number) => {
    setVerifyingRecordId(recordId);
    try {
      const updated = await verifyFfnRecordReport(recordId);
      queryClient.invalidateQueries(["ffnRecords"]);
      setSelected((prev) => (prev?.id === recordId ? updated : prev));
    } catch {
      // surfaced by the unchanged verification chip; refresh shows state
    } finally {
      setVerifyingRecordId(null);
    }
  };

  const columns = useMemo<WorkflowRunsColumn<FfnRecordRow>[]>(
    () => [
      { key: "id", header: "#", render: (record) => record.id },
      {
        key: "title",
        header: "Title",
        render: (record) => (
          <Typography variant="body2" noWrap sx={{ maxWidth: 360 }}>
            {record.title}
          </Typography>
        ),
      },
      {
        key: "plant",
        header: "Plant",
        render: (record) => record.plant_name ?? record.plant_id,
      },
      {
        key: "status",
        header: "Status",
        render: (record) => (
          <Chip
            label={record.status.replace(/_/g, " ")}
            size="small"
            color={STATUS_CHIP_COLOR[record.status] ?? "default"}
            variant="outlined"
          />
        ),
      },
      {
        key: "challenge",
        header: "Challenge",
        render: (record) =>
          record.challenge ? (
            <Chip
              label={record.challenge.passed ? "passed" : "failed"}
              size="small"
              color={record.challenge.passed ? "success" : "error"}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        key: "verification",
        header: "Verification",
        render: (record) =>
          record.report_verification?.status === "complete" &&
          record.report_verification.tier ? (
            <Chip
              label={record.report_verification.tier.replace(/_/g, " ")}
              size="small"
              color={
                VERIFICATION_TIER_COLOR[record.report_verification.tier] ??
                "default"
              }
              data-testid={`ffn-record-verification-${record.id}`}
            />
          ) : record.report_verification?.status === "pending" ? (
            <Chip label="verifying…" size="small" variant="outlined" />
          ) : record.report_verification?.status === "failed" ? (
            <Chip
              label="verification failed"
              size="small"
              color="error"
              variant="outlined"
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        key: "updated",
        header: "Updated",
        render: (record) => (
          <Typography variant="caption" color="text.secondary">
            {record.updated_date
              ? new Date(record.updated_date).toLocaleDateString()
              : "—"}
          </Typography>
        ),
      },
      {
        key: "report",
        header: "Report",
        align: "right",
        render: (record) =>
          record.report_artifact_id ? (
            <Tooltip title="Download final report (.docx)">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadReportArtifact(
                    record.report_artifact_id as number,
                    `50_59_record_${record.id}_report.docx`,
                  );
                }}
                data-testid={`ffn-record-download-${record.id}`}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : record.status === "cancelled" ? (
            <Typography variant="caption" color="text.disabled" sx={{ pr: 1 }}>
              cancelled
            </Typography>
          ) : (
            <Tooltip title="Send the 50.59 Evaluation Lead to finish this record (research, challenge loop, report, finalization)">
              <span>
                <Button
                  size="small"
                  startIcon={
                    resumingRecordId === record.id ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <PlayArrowIcon fontSize="small" />
                    )
                  }
                  disabled={resumingRecordId !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    resumeRecord(record.id);
                  }}
                  data-testid={`ffn-record-resume-${record.id}`}
                >
                  Resume
                </Button>
              </span>
            </Tooltip>
          ),
      },
    ],
    [resumeRecord, resumingRecordId],
  );

  return (
    <WorkflowRunsPage<FfnRecordRow>
      title="10 CFR 50.59 Records"
      testIdPrefix={FIFTY_FIFTY_NINE_PRODUCT.testIdPrefix}
      filters={[
        {
          key: "plant",
          label: "Plant",
          options: plants.map((p) => ({ value: p.id, label: p.name })),
          value: plantFilter,
          onChange: (value) => setPlantFilter(value as number | ""),
          allLabel: "All plants",
        },
        {
          key: "status",
          label: "Status",
          options: STATUS_OPTIONS.map((s) => ({
            value: s,
            label: s.replace(/_/g, " "),
          })),
          value: statusFilter,
          onChange: (value) => setStatusFilter(value as string),
          allLabel: "All statuses",
        },
      ]}
      columns={columns}
      rows={records}
      rowKey={(record) => record.id}
      isLoading={isFetching}
      isError={isError}
      onRefresh={() => refetch()}
      onRowClick={(record) => setSelected(record)}
      emptyState={
        <>
          <WorkflowRunsEmptyState
            message="No 50.59 records yet. Start one from the"
            linkHref={FIFTY_FIFTY_NINE_PRODUCT.basePath}
            linkLabel="50.59 Evaluation page"
            onNavigate={() => history.push(FIFTY_FIFTY_NINE_PRODUCT.basePath)}
          />
          .
        </>
      }
      detail={
        <FfnRecordDetailDialog
          record={selected}
          onClose={() => setSelected(null)}
          onResume={resumeRecord}
          resumingRecordId={resumingRecordId}
          onVerify={handleVerify}
          verifyingRecordId={verifyingRecordId}
        />
      }
    />
  );
};

export default FfnRecordsDashboardPage;
